import { useState, useEffect } from 'react';
import axiosInstance from '../../../utils/axios';
import { SERVER } from '../../../hooks/config';
import { 
  MdAdd, 
  MdSearch, 
  MdFilterList, 
  MdEdit, 
  MdDelete, 
  MdVisibility,
  MdClose,
  MdSave,
  MdCancel
} from 'react-icons/md';
import './Products.scss';

const Products = () => {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [showNewProducts, setShowNewProducts] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [viewingProduct, setViewingProduct] = useState(null);
  const [guideTailleFile, setGuideTailleFile] = useState(null);
  const [existingGuideTaille, setExistingGuideTaille] = useState('');
  // Form state for add/edit
  const [formData, setFormData] = useState({
    name: '',
    code: '',
    fullname: '',
    general_description: '',
    description: '',
    materiel: '',
    finition: '',
    dimension: '',
    ideal: '',
    price: '',
    promotion: '',
    shipping_cost: '7.00',
    new_product: false,
    category_id: '',
    tailles: '',
    guide_taille: ''
  });

  // File state for uploads
  const [bannerFile, setBannerFile] = useState(null);
  const [imageFiles, setImageFiles] = useState([]);
  const [existingBanner, setExistingBanner] = useState('');
  const [existingImages, setExistingImages] = useState([]);

  const productsPerPage = 10;

  useEffect(() => {
    fetchProducts();
    fetchCategories();
  }, [currentPage, searchTerm, selectedCategory, showNewProducts]);

  const fetchProducts = async () => {
    try {
      setLoading(true);
      const params = {
        page: currentPage,
        limit: productsPerPage,
        search: searchTerm || undefined,
        category_id: selectedCategory || undefined,
        new_product: showNewProducts ? 'true' : undefined
      };

      const response = await axiosInstance.get('/products', { params });
      setProducts(response.data.products);
      setTotalPages(response.data.pagination.total_pages);
    } catch (error) {
      console.error('Error fetching products:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await axiosInstance.get('/categories');
      setCategories(response.data);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };

  const handleCategoryFilter = (e) => {
    setSelectedCategory(e.target.value);
    setCurrentPage(1);
  };

  const handleResetFilters = () => {
    setSearchTerm('');
    setSelectedCategory('');
    setShowNewProducts(false);
    setCurrentPage(1);
  };

  const handleAddProduct = async (e) => {
    e.preventDefault();
    try {
      const formDataToSend = new FormData();
      
      // Add text fields
      Object.keys(formData).forEach(key => {
        if (formData[key] !== '') {
          formDataToSend.append(key, formData[key]);
        }
      });

      // Add files
      if (bannerFile) {
        formDataToSend.append('banner', bannerFile);
      }
      if (imageFiles.length > 0) {
        imageFiles.forEach(file => {
          formDataToSend.append('images', file);
        });
      }

      if (guideTailleFile) {
        formDataToSend.append('guide_taille', guideTailleFile);
      }

      await axiosInstance.post('/products', formDataToSend, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      setShowAddModal(false);
      resetForm();
      fetchProducts();
    } catch (error) {
      console.error('Error adding product:', error);
      alert(error.response?.data?.error || 'Error adding product');
    }
  };

  const handleEditProduct = async (e) => {
    e.preventDefault();
    try {
      const formDataToSend = new FormData();
      
      // Add text fields
      Object.keys(formData).forEach(key => {
        if (formData[key] !== '') {
          formDataToSend.append(key, formData[key]);
        }
      });

      // Add files
      if (bannerFile) {
        formDataToSend.append('banner', bannerFile);
      }
      if (guideTailleFile) {
        formDataToSend.append('guide_taille', guideTailleFile);
      }
      if (imageFiles.length > 0) {
        imageFiles.forEach(file => {
          formDataToSend.append('images', file);
        });
      }

      await axiosInstance.put(`/products/${editingProduct.id}`, formDataToSend, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      setEditingProduct(null);
      resetForm();
      fetchProducts();
    } catch (error) {
      console.error('Error updating product:', error);
      alert(error.response?.data?.error || 'Error updating product');
    }
  };

  const handleDeleteProduct = async (productId) => {
    if (window.confirm('Are you sure you want to delete this product?')) {
      try {
        await axiosInstance.delete(`/products/${productId}`);
        fetchProducts();
      } catch (error) {
        console.error('Error deleting product:', error);
        alert(error.response?.data?.error || 'Error deleting product');
      }
    }
  };

  const startEdit = (product) => {
    setEditingProduct(product);
    console.log(product)
    setFormData({
      name: product.name || '',
      code: product.code || '',
      fullname: product.fullname || '',
      general_description: product.general_description || '',
      description: product.description || '',
      materiel: product.materiel || '',
      finition: product.finition || '',
      dimension: product.dimension || '',
      ideal: product.ideal || '',
      price: product.price || '',
      promotion: product.promotion || '',
      shipping_cost: product.shipping_cost ? String(product.shipping_cost) : '7.00',
      new_product: product.new_product || false,
      category_id: product.category_id || '',
      tailles: product.tailles ? product.tailles.join(', ') : '',
    });
    setExistingGuideTaille(product.guide_taille || '');
    setGuideTailleFile(null);
    setExistingBanner(product.banner || '');
    setExistingImages(product.images || []);
    setBannerFile(null);
    setImageFiles([]);
  };

  const startView = (product) => {
    setViewingProduct(product);
  };

  const resetForm = () => {
    setFormData({
      name: '',
      code: '',
      fullname: '',
      general_description: '',
      description: '',
      materiel: '',
      finition: '',
      dimension: '',
      ideal: '',
      price: '',
      promotion: '',
      shipping_cost: '7.00',
      new_product: false,
      category_id: '',
      tailles: ''
    });
    setBannerFile(null);
    setImageFiles([]);
    setExistingBanner('');
    setExistingImages([]);
    setGuideTailleFile(null);
    setExistingGuideTaille('');
  };

  const handleInputChange = (e) => {
    const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setFormData({
      ...formData,
      [e.target.name]: value
    });
  };

  const formatCurrency = (price) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'TND'
    }).format(price);
  };

  const truncateText = (text, maxLength = 50) => {
    if (!text) return '';
    return text.length > maxLength ? text.substring(0, maxLength) + '...' : text;
  };

  if (loading) {
    return (
      <div className="products-loading">
        <div className="loading-spinner"></div>
        <p>Loading products...</p>
      </div>
    );
  }

  return (
    <div className="admin-products">
      {/* Header */}
      <div className="products-header">
        <div className="header-left">
          <h2>Products Management</h2>
          <p>Manage your product catalog</p>
        </div>
        <button 
          className="add-product-btn"
          onClick={() => setShowAddModal(true)}
        >
          <MdAdd />
          Add Product
        </button>
      </div>

      {/* Filters */}
      <div className="products-filters">
        <div className="search-box">
          <MdSearch />
          <input
            type="text"
            placeholder="Search products..."
            value={searchTerm}
            onChange={handleSearch}
          />
        </div>
        
        <div className="filter-box">
          <MdFilterList />
          <select value={selectedCategory} onChange={handleCategoryFilter}>
            <option value="">All Categories</option>
            {categories.map(category => (
              <option key={category.id} value={category.id}>
                {category.name}
              </option>
            ))}
          </select>
        </div>

        <div className="filter-box">
          <label className="checkbox-label">
            <input
              type="checkbox"
              checked={showNewProducts}
              onChange={(e) => setShowNewProducts(e.target.checked)}
            />
            New Products Only
          </label>
        </div>

        <button 
          className="reset-filters-btn"
          onClick={handleResetFilters}
          type="button"
        >
          Reset Filters
        </button>
      </div>

      {/* Products Table */}
      <div className="products-table-container">
        <table className="products-table">
          <thead>
            <tr>
              <th>Image</th>
              <th>Name</th>
              <th>Code</th>
              <th>Category</th>
              <th>Price</th>
              <th>Promotion</th>
              <th>New</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {products.map(product => (
              <tr key={product.id}>
                <td>
                  <div className="product-image">
                    {product.banner ? (
                      <img src={SERVER+product.banner} alt={product.name} />
                    ) : (
                      <div className="no-image">No Image</div>
                    )}
                  </div>
                </td>
                <td>
                  <div className="product-name">
                    <strong>{product.name}</strong>
                    {product.fullname && (
                      <span className="product-fullname">
                        {truncateText(product.fullname, 40)}
                      </span>
                    )}
                  </div>
                </td>
                <td>
                  <code className="product-code">{product.code}</code>
                </td>
                <td>
                  <span className="product-category">
                    {product.category_name || 'Uncategorized'}
                  </span>
                </td>
                <td>
                  <span className="product-price">
                    {formatCurrency(product.price)}
                  </span>
                </td>
                <td>
                  {product.promotion > 0 ? (
                    <span className="product-promotion">
                      {product.promotion}%
                    </span>
                  ) : (
                    <span className="no-promotion">-</span>
                  )}
                </td>
                <td>
                  {product.new_product ? (
                    <span className="new-product-badge">New</span>
                  ) : (
                    <span className="not-new">-</span>
                  )}
                </td>
                <td>
                  <div className="action-buttons">
                    <button
                      className="action-btn view-btn"
                      onClick={() => startView(product)}
                      title="View Details"
                    >
                      <MdVisibility />
                    </button>
                    <button
                      className="action-btn edit-btn"
                      onClick={() => startEdit(product)}
                      title="Edit Product"
                    >
                      <MdEdit />
                    </button>
                    <button
                      className="action-btn delete-btn"
                      onClick={() => handleDeleteProduct(product.id)}
                      title="Delete Product"
                    >
                      <MdDelete />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {products.length === 0 && (
          <div className="no-products">
            <p>No products found</p>
          </div>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="pagination">
          <button
            onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
            disabled={currentPage === 1}
          >
            Previous
          </button>
          
          {Array.from({ length: totalPages }, (_, i) => (
            <button
              key={i + 1}
              onClick={() => setCurrentPage(i + 1)}
              className={currentPage === i + 1 ? 'active' : ''}
            >
              {i + 1}
            </button>
          ))}
          
          <button
            onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
            disabled={currentPage === totalPages}
          >
            Next
          </button>
        </div>
      )}

      {/* Add Product Modal */}
      {showAddModal && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h3>Add New Product</h3>
              <button onClick={() => setShowAddModal(false)}>
                <MdClose />
              </button>
            </div>
            <form onSubmit={handleAddProduct}>
              <div className="form-row">
                <div className="form-group">
                  <label>Name *</label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Code *</label>
                  <input
                    type="text"
                    name="code"
                    value={formData.code}
                    onChange={handleInputChange}
                    required
                  />
                </div>
              </div>
              
              <div className="form-group">
                <label>Full Name</label>
                <input
                  type="text"
                  name="fullname"
                  value={formData.fullname}
                  onChange={handleInputChange}
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Price *</label>
                  <input
                    type="number"
                    name="price"
                    value={formData.price}
                    onChange={handleInputChange}
                    step="0.01"
                    min="0"
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Promotion (%)</label>
                  <input
                    type="number"
                    name="promotion"
                    value={formData.promotion}
                    onChange={handleInputChange}
                    step="0.01"
                    min="0"
                    max="100"
                  />
                </div>
                <div className="form-group">
                  <label>Shipping Cost (DT)</label>
                  <input
                    type="number"
                    name="shipping_cost"
                    value={formData.shipping_cost}
                    onChange={handleInputChange}
                    step="0.01"
                    min="0"
                  />
                </div>
              </div>

              <div className="form-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    name="new_product"
                    checked={formData.new_product}
                    onChange={handleInputChange}
                  />
                  New Product
                </label>
              </div>

              <div className="form-group">
                <label>Category</label>
                <select
                  name="category_id"
                  value={formData.category_id}
                  onChange={handleInputChange}
                >
                  <option value="">Select Category</option>
                  {categories.map(category => (
                    <option key={category.id} value={category.id}>
                      {category.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label>General Description</label>
                <textarea
                  name="general_description"
                  value={formData.general_description}
                  onChange={handleInputChange}
                  rows="3"
                />
              </div>

              <div className="form-group">
                <label>Description</label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  rows="4"
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Material</label>
                  <input
                    type="text"
                    name="materiel"
                    value={formData.materiel}
                    onChange={handleInputChange}
                  />
                </div>
                <div className="form-group">
                  <label>Finition</label>
                  <input
                    type="text"
                    name="finition"
                    value={formData.finition}
                    onChange={handleInputChange}
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Dimensions</label>
                  <input
                    type="text"
                    name="dimension"
                    value={formData.dimension}
                    onChange={handleInputChange}
                  />
                </div>
                <div className="form-group">
                  <label>Ideal For</label>
                  <input
                    type="text"
                    name="ideal"
                    value={formData.ideal}
                    onChange={handleInputChange}
                  />
                </div>
              </div>
              <div className="form-group">
                <label>Sizes (comma-separated)</label>
                <input
                  type="text"
                  name="tailles"
                  value={formData.tailles}
                  onChange={handleInputChange}
                  placeholder="S, M, L, XL"
                />
              </div>
              <div className="form-group">
                <label>Size Guide Image</label>
                <input
                  type="file"
                  name="guide_taille"
                  accept="image/*"
                  onChange={(e) => setGuideTailleFile(e.target.files[0] || null)}
                />
                {existingGuideTaille && (
                  <div className="current-image-preview">
                    <p>Current Size Guide:</p>
                    <img src={SERVER+existingGuideTaille} alt="Size guide" className="preview-image" />
                  </div>
                )}
              </div>
              <div className="form-group">
                <label>Banner Image</label>
                <input
                  type="file"
                  name="banner"
                  accept="image/*"
                  onChange={(e) => setBannerFile(e.target.files[0] || null)}
                />
              </div>

              <div className="form-group">
                <label>Product Images (up to 3)</label>
                <input
                  type="file"
                  name="images"
                  accept="image/*"
                  multiple
                  onChange={(e) => setImageFiles(Array.from(e.target.files || []))}
                />
              </div>

              <div className="modal-actions">
                <button type="button" onClick={() => setShowAddModal(false)}>
                  Cancel
                </button>
                <button type="submit" className="primary">
                  Add Product
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Product Modal */}
      {editingProduct && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h3>Edit Product</h3>
              <button onClick={() => setEditingProduct(null)}>
                <MdClose />
              </button>
            </div>
            <form onSubmit={handleEditProduct}>
              <div className="form-row">
                <div className="form-group">
                  <label>Name *</label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Code *</label>
                  <input
                    type="text"
                    name="code"
                    value={formData.code}
                    onChange={handleInputChange}
                    required
                  />
                </div>
              </div>
              
              <div className="form-group">
                <label>Full Name</label>
                <input
                  type="text"
                  name="fullname"
                  value={formData.fullname}
                  onChange={handleInputChange}
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Price *</label>
                  <input
                    type="number"
                    name="price"
                    value={formData.price}
                    onChange={handleInputChange}
                    step="0.01"
                    min="0"
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Promotion (%)</label>
                  <input
                    type="number"
                    name="promotion"
                    value={formData.promotion}
                    onChange={handleInputChange}
                    step="0.01"
                    min="0"
                    max="100"
                  />
                </div>
                <div className="form-group">
                  <label>Shipping Cost (DT)</label>
                  <input
                    type="number"
                    name="shipping_cost"
                    value={formData.shipping_cost}
                    onChange={handleInputChange}
                    step="0.01"
                    min="0"
                  />
                </div>
              </div>

              <div className="form-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    name="new_product"
                    checked={formData.new_product}
                    onChange={handleInputChange}
                  />
                  New Product
                </label>
              </div>

              <div className="form-group">
                <label>Category</label>
                <select
                  name="category_id"
                  value={formData.category_id}
                  onChange={handleInputChange}
                >
                  <option value="">Select Category</option>
                  {categories.map(category => (
                    <option key={category.id} value={category.id}>
                      {category.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label>General Description</label>
                <textarea
                  name="general_description"
                  value={formData.general_description}
                  onChange={handleInputChange}
                  rows="3"
                />
              </div>

              <div className="form-group">
                <label>Description</label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  rows="4"
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Material</label>
                  <input
                    type="text"
                    name="materiel"
                    value={formData.materiel}
                    onChange={handleInputChange}
                  />
                </div>
                <div className="form-group">
                  <label>Finition</label>
                  <input
                    type="text"
                    name="finition"
                    value={formData.finition}
                    onChange={handleInputChange}
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Dimensions</label>
                  <input
                    type="text"
                    name="dimension"
                    value={formData.dimension}
                    onChange={handleInputChange}
                  />
                </div>
                <div className="form-group">
                  <label>Ideal For</label>
                  <input
                    type="text"
                    name="ideal"
                    value={formData.ideal}
                    onChange={handleInputChange}
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Sizes (comma-separated)</label>
                <input
                  type="text"
                  name="tailles"
                  value={formData.tailles}
                  onChange={handleInputChange}
                  placeholder="S, M, L, XL"
                />
              </div>

              <div className="form-group">
                <label>Size Guide Image</label>
                <input
                  type="file"
                  name="guide_taille"
                  accept="image/*"
                  onChange={(e) => setGuideTailleFile(e.target.files[0] || null)}
                />
                {existingGuideTaille && (
                  <div className="current-image-preview">
                    <p>Current Size Guide:</p>
                    <img src={SERVER+existingGuideTaille} alt="Size guide" className="preview-image" />
                  </div>
                )}
              </div>

              <div className="form-group">
                <label>Banner Image</label>
                <input
                  type="file"
                  name="banner"
                  accept="image/*"
                  onChange={(e) => setBannerFile(e.target.files[0] || null)}
                />
                {existingBanner && (
                  <div className="current-image-preview">
                    <p>Current Banner:</p>
                    <img src={SERVER+existingBanner} alt="Current banner" className="preview-image" />
                  </div>
                )}
              </div>

              <div className="form-group">
                <label>Product Images (up to 3)</label>
                <input
                  type="file"
                  name="images"
                  accept="image/*"
                  multiple
                  onChange={(e) => setImageFiles(Array.from(e.target.files || []))}
                />
                {existingImages.length > 0 && (
                  <div className="current-images-preview">
                    <p>Current Images:</p>
                    <div className="preview-grid">
                      {existingImages.map((image, index) => (
                        <img 
                          key={index} 
                          src={SERVER+image} 
                          alt={`Current image ${index + 1}`}
                          className="preview-image"
                        />
                      ))}
                    </div>
                  </div>
                )}
              </div>

              <div className="modal-actions">
                <button type="button" onClick={() => setEditingProduct(null)}>
                  Cancel
                </button>
                <button type="submit" className="primary">
                  Update Product
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* View Product Modal */}
      {viewingProduct && (
        <div className="modal-overlay">
          <div className="modal view-modal">
            <div className="modal-header">
              <h3>Product Details</h3>
              <button onClick={() => setViewingProduct(null)}>
                <MdClose />
              </button>
            </div>
            <div className="product-details">
              <div className="product-header">
                {viewingProduct.banner && (
                  <img 
                    src={SERVER+viewingProduct.banner} 
                    alt={viewingProduct.name}
                    className="product-banner"
                  />
                )}
                <div className="product-info">
                  <h4>{viewingProduct.name}</h4>
                  <p className="product-code">{viewingProduct.code}</p>
                  <p className="product-price">
                    {formatCurrency(viewingProduct.price)}
                    {viewingProduct.promotion > 0 && (
                      <span className="promotion-badge">
                        {viewingProduct.promotion}% OFF
                      </span>
                    )}
                  </p>
                  {viewingProduct.shipping_cost && (
                    <p className="shipping-cost-info">
                      Frais de livraison: {formatCurrency(viewingProduct.shipping_cost)}
                    </p>
                  )}
                </div>
              </div>

              <div className="product-sections">
                {viewingProduct.fullname && (
                  <div className="section">
                    <h5>Full Name</h5>
                    <p>{viewingProduct.fullname}</p>
                  </div>
                )}

                {viewingProduct.general_description && (
                  <div className="section">
                    <h5>General Description</h5>
                    <p>{viewingProduct.general_description}</p>
                  </div>
                )}

                {viewingProduct.description && (
                  <div className="section">
                    <h5>Description</h5>
                    <p>{viewingProduct.description}</p>
                  </div>
                )}

                <div className="section">
                  <h5>Specifications</h5>
                  <div className="specs-grid">
                    {viewingProduct.materiel && (
                      <div className="spec-item">
                        <span className="spec-label">Material:</span>
                        <span className="spec-value">{viewingProduct.materiel}</span>
                      </div>
                    )}
                    {viewingProduct.finition && (
                      <div className="spec-item">
                        <span className="spec-label">Finition:</span>
                        <span className="spec-value">{viewingProduct.finition}</span>
                      </div>
                    )}
                    {viewingProduct.dimension && (
                      <div className="spec-item">
                        <span className="spec-label">Dimensions:</span>
                        <span className="spec-value">{viewingProduct.dimension}</span>
                      </div>
                    )}
                    {viewingProduct.ideal && (
                      <div className="spec-item">
                        <span className="spec-label">Ideal For:</span>
                        <span className="spec-value">{viewingProduct.ideal}</span>
                      </div>
                    )}
                    {viewingProduct.tailles && viewingProduct.tailles.length > 0 && (
                      <div className="section">
                        <h5>Available Sizes</h5>
                        <div className="sizes-list">
                          {viewingProduct.tailles.map((size, index) => (
                            <span key={index} className="size-tag">{size}</span>
                          ))}
                        </div>
                      </div>
                    )}

                    {viewingProduct.guide_taille && (
                      <div className="section">
                        <h5>Size Guide</h5>
                        <img 
                          src={SERVER+viewingProduct.guide_taille} 
                          alt="Size guide"
                          className="size-guide-image"
                        />
                      </div>
                    )}
                  </div>
                </div>

                {viewingProduct.images && viewingProduct.images.length > 0 && (
                  <div className="section">
                    <h5>Additional Images</h5>
                    <div className="product-images">
                      {viewingProduct.images.map((image, index) => (
                        <img 
                          key={index} 
                          src={SERVER+image} 
                          alt={`${viewingProduct.name} ${index + 1}`}
                        />
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Products;